import { useEffect, useState } from "react";
import { apiClient } from "@/App";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { BellRing, Plus, X, Save } from "lucide-react";
import { errorText, SWITCH_CLS } from "@/lib/calendar";

const label = (m) => (m % 1440 === 0 ? `${m / 1440 === 1 ? "24 hours" : `${m / 1440} days`}` : m % 60 === 0 ? `${m / 60} hour${m > 60 ? "s" : ""}` : `${m} minutes`);
const UNITS = { minutes: 1, hours: 60, days: 1440 };

export const ReminderSettingsCard = ({ onSaved }) => {
  const [s, setS] = useState(null);
  const [amount, setAmount] = useState("");
  const [unit, setUnit] = useState("hours");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    apiClient.get("/reminders/settings").then(({ data }) => setS(data)).catch(() => toast.error("Failed to load reminder settings"));
  }, []);

  if (!s) return null;
  const toggle = (k) => (v) => setS({ ...s, [k]: v });
  const addOffset = () => {
    const m = Math.round(Number(amount) * UNITS[unit]);
    if (!m) return;
    setS({ ...s, offsets_minutes: [...new Set([...s.offsets_minutes, m])].sort((a, b) => b - a) });
    setAmount("");
  };
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await apiClient.put("/reminders/settings", s);
      setS(data);
      toast.success("Reminder settings saved");
      onSaved?.();
    } catch (e) {
      toast.error(errorText(e, "Failed to save settings"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 space-y-4" data-testid="reminder-settings-card">
      <div className="flex items-center justify-between">
        <h3 className="font-heading font-bold text-lg flex items-center gap-2"><BellRing className="w-5 h-5 text-cyan-600" />Automatic Reminders</h3>
        <Switch className={SWITCH_CLS} checked={s.enabled} onCheckedChange={toggle("enabled")} data-testid="reminders-enabled-switch" />
      </div>
      <div>
        <p className="text-sm font-semibold mb-2">Send reminders before each pickup</p>
        <div className="flex flex-wrap gap-2" data-testid="reminder-offsets">
          {s.offsets_minutes.map((m) => (
            <span key={m} className="inline-flex items-center gap-1 bg-cyan-50 border border-cyan-200 text-cyan-800 text-sm rounded-full px-3 py-1" data-testid={`reminder-offset-${m}`}>
              {label(m)}
              <button type="button" onClick={() => setS({ ...s, offsets_minutes: s.offsets_minutes.filter((x) => x !== m) })} data-testid={`remove-offset-${m}`}>
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
        </div>
        <div className="flex gap-2 mt-3 max-w-sm">
          <Input type="number" min="1" placeholder="e.g. 4" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="add-offset-amount" />
          <Select value={unit} onValueChange={setUnit}>
            <SelectTrigger className="w-32" data-testid="add-offset-unit"><SelectValue /></SelectTrigger>
            <SelectContent>{Object.keys(UNITS).map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
          </Select>
          <Button variant="outline" onClick={addOffset} data-testid="add-offset-btn"><Plus className="w-4 h-4" /></Button>
        </div>
      </div>
      <div className="grid sm:grid-cols-3 gap-3">
        {[["send_to_dispatch", "Dispatch"], ["send_to_driver", "Assigned driver"], ["send_to_customer", "Customer"]].map(([k, l]) => (
          <label key={k} className="flex items-center justify-between rounded-xl border border-slate-200 px-3 py-2 text-sm">
            {l}<Switch className={SWITCH_CLS} checked={s[k]} onCheckedChange={toggle(k)} data-testid={`reminder-${k}`} />
          </label>
        ))}
      </div>
      <p className="text-xs text-slate-500">Delivered in-app and by browser push. The scheduler checks every 15 minutes, so a reminder may arrive up to 15 minutes early — never late.</p>
      <Button onClick={save} disabled={busy} className="bg-cyan-600 hover:bg-cyan-700" data-testid="save-reminder-settings-btn">
        <Save className="w-4 h-4 mr-2" />Save Settings
      </Button>
    </div>
  );
};

export default ReminderSettingsCard;
